import React from 'react';
import { notification } from 'tea-component';
import { LANGUAGE_EN, LANGUAGE_CN } from '@intelligence/_config/config';
interface ICascaderOption {
  value: string;
  children?: ICascaderOption[] | null;
}

/**
 * 获取Segment For Sales筛选器的全选value
 * @param options Segment For Sales筛选器的options数据
 * @return Segment For Sales筛选器的全选value
 */
export const getAllValuesOfCascader = <T extends ICascaderOption>(options?: T[] | null): Record<string, string[]> => {
  if (!Array.isArray(options) || !options.length) return {};

  return options.reduce((acc, cur) => {
    const { value, children: subOptions } = cur || {};
    if (!value) return acc;
    const subValues = subOptions?.map((subOption) => subOption?.value) || [];
    return { ...acc, [value]: subValues };
  }, {});
};

/**
 * 检测Segment For Sales筛选器是否每个数据源都是仅勾选了Base Game，或者没有勾选任何Segment
 * @param payload Segment For Sales筛选器的value
 * @return Segment For Sales筛选器是否每个数据源都是仅勾选了Base Game，或者没有勾选任何Segment
 */
export const checkIsSegmentForSalesOnlyCheckedBaseGame = (payload?: { mscience: string[]; gsd: string[] }): boolean => {
  if (!payload) return false;
  const { mscience: mscienceValues, gsd: gsdValues } = payload;

  return [mscienceValues, gsdValues].every((values) => {
    if (!Array.isArray(values) || !values.length) return true;
    return values.length === 1 && values[0]?.toLowerCase() === 'full game';
  });
};

/**
 * 自动勾选全部产品分类后的提示render
 * @param props 参数对象
 * @param props.locale 当前语言环境
 * @param props.onCancel 点击Cancel的回调
 */
const renderAutoAdjustSegmentForSalesTips = (props: { locale?: string; onCancel?: () => void }) => {
  const { locale = LANGUAGE_EN, onCancel } = props;

  const handleCancel = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onCancel?.();
  };

  const tips: Record<string, React.ReactElement> = {
    [LANGUAGE_EN]: (
      <span>
        No data available for <b>Base Game</b> under Segment for Sales filter. <b>All segments</b> have been
        automatically selected. Click{' '}
        <a style={{ fontWeight: 700 }} onClick={handleCancel}>
          Cancel
        </a>{' '}
        to return to <b>Base Game</b>
      </span>
    ),
    [LANGUAGE_CN]: (
      <span>
        筛选器产品分类为<b>游戏拷贝</b>无数据，已为您自动选择<b>全部产品分类</b>
        ，点击
        <a style={{ fontWeight: 700 }} onClick={handleCancel}>
          取消
        </a>
        回到默认选项
        <b>游戏拷贝</b>
      </span>
    ),
  };

  return tips[locale] || tips[LANGUAGE_EN];
};

/**
 * 自动勾选全部产品分类后的提示
 * @param props 参数对象
 * @param props.locale 当前语言环境
 * @param props.onCancel 点击Cancel的回调
 */
export const notificationAutoAdjustSegmentForSalesTips = (props: { locale?: string; onCancel?: () => void }) => {
  const { locale = LANGUAGE_EN, onCancel } = props;

  const { destroy } = notification.success({
    unique: true,
    className: 'dbi-notification',
    description: renderAutoAdjustSegmentForSalesTips({
      locale,
      onCancel: () => {
        onCancel?.();
        destroy?.();
      },
    }),
  });
};

// DataBrain校准指标
export const CALIBRATION_METRICS = [
  'databrain__total_revenue',
  'databrain__lifetime_total_revenue',
  'databrain__base_game_sales_units',
  'databrain__lifetime_base_game_sales_units',
];

/**
 * 判断当前勾选指标是否仅为DataBrain校准指标
 * @param metrics 当前勾选的指标
 * @return 当前勾选指标是否仅为DataBrain校准指标
 */
export const calcIsDataBrainCalibrationMetricsOnly = (metrics: string[]): boolean => {
  if (!Array.isArray(metrics) || metrics.length !== CALIBRATION_METRICS.length) return false;
  return metrics.every((metric) => CALIBRATION_METRICS.includes(metric));
};

/**
 * 判断当前勾选的Segment For Sales是不是全选
 * @param options Segment For Sales筛选器的options数据
 * @param value Segment For Sales筛选器的value
 * @return 当前勾选的Segment For Sales是不是全选
 */
export const calcIsSegmentForSalesAllChecked = (options?: any[], value?: IRecord<string[]>): boolean => {
  if (!Array.isArray(options) || options.length === 0) {
    return false;
  }

  if (!value) {
    return false;
  }

  const allValues = getAllValuesOfCascader(options);
  return Object.keys(allValues).every((key) => {
    const subAllValues = allValues[key];
    const checkedValues = value[key] || [];
    return (
      subAllValues.length === checkedValues.length && subAllValues.every((subValue) => checkedValues.includes(subValue))
    );
  });
};
